import { motion } from "framer-motion";
import { ShoppingCart, Flower2, Package, Route } from "lucide-react";

const IMGS = {
  drive:
    "https://images.unsplash.com/photo-1588964895597-cfccd6e2dbf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxncm9jZXJ5JTIwcGlja3VwJTIwYmFnc3xlbnwwfHx8fDE3ODY4NzgxMDN8MA&ixlib=rb-4.1.0&q=85",
  fleurs:
    "https://images.unsplash.com/photo-1503652601-557d07733ddc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMGZsb3dlciUyMGJhc2tldCUyMGRlbGl2ZXJ5fGVufDB8fHx8MTc4Njg3OTkxOHww&ixlib=rb-4.1.0&q=85",
  colis:
    "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjb3VyaWVyJTIwZGVsaXZlcnklMjBwYXJjZWwlMjBwYWNrYWdlJTIwYm94fGVufDB8fHx8MTc4ODYyOTg5N3ww&ixlib=rb-4.1.0&q=85",
  course:
    "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjb3VyaWVyJTIwZGVsaXZlcnklMjBwYXJjZWwlMjBwYWNrYWdlJTIwYm94fGVufDB8fHx8MTc4ODYyOTg5N3ww&ixlib=rb-4.1.0&q=85",
};

const CARDS = [
  {
    key: "drive",
    icon: ShoppingCart,
    title: "Drive & courses",
    text: "Vous commandez en drive, je récupère vos sacs et je les monte jusqu'à votre porte. Idéal quand les courses sont lourdes ou que sortir devient compliqué.",
    span: "lg:col-span-7",
    price: "5 à 15 €",
  },
  {
    key: "fleurs",
    icon: Flower2,
    title: "Fleurs & cadeaux",
    text: "Un bouquet pour un anniversaire, un cadeau à déposer en main propre : je le livre avec le soin qu'il mérite.",
    span: "lg:col-span-5",
    price: "5 à 15 €",
  },
  {
    key: "colis",
    icon: Package,
    title: "Colis & retraits",
    text: "Point relais, consigne, La Poste : je récupère vos colis à votre place et vous les apporte.",
    span: "lg:col-span-5",
    price: "5 à 15 €",
  },
  {
    key: "course",
    icon: Route,
    title: "Course sur mesure, d'un point A à un point B",
    text: "Un médicament à la pharmacie, un dossier à déposer, un objet à transporter : décrivez votre besoin, je m'occupe du trajet.",
    span: "lg:col-span-7",
    price: "5 à 15 €",
  },
];

export default function Services() {
  return (
    <section id="services" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 max-w-2xl"
        >
          <span className="mb-4 inline-flex w-fit rounded-full bg-terra/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-terra-dark">
            Ce que je livre
          </span>
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Quatre services, une même promesse : <em className="italic text-forest">vous soulager</em>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 md:gap-8">
          {CARDS.map((card, i) => (
            <motion.article
              key={card.key}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-ink/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10 ${card.span}`}
              data-testid={`service-card-${card.key}`}
            >
              <div className="overflow-hidden">
                <img
                  src={IMGS[card.key]}
                  alt={card.title}
                  loading="lazy"
                  className="aspect-[21/9] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6 sm:p-8">
                <div className="mb-4 flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/10 text-forest">
                    <card.icon className="h-6 w-6" />
                  </span>
                  <span className="rounded-full bg-terra/10 px-3 py-1 text-sm font-semibold text-terra-dark">{card.price}</span>
                </div>
                <h3 className="mb-2 font-serif text-xl font-medium text-ink sm:text-2xl">{card.title}</h3>
                <p className="text-base leading-relaxed text-ink/65">{card.text}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
